/* eslint-disable react-refresh/only-export-components --
 * This module deliberately pairs the container component with its imperative
 * `showToast` trigger so any module can raise a toast without threading props.
 */
import { useEffect, useState } from 'react'
import { CheckCircle, XCircle, X } from 'lucide-react'

export interface ToastMessage {
  id: number
  type: 'success' | 'error'
  title: string
  body?: string
}

let _nextId = 0

/** Simple in-memory store — siblings can push toasts via showToast() */
const listeners: Array<(msg: ToastMessage) => void> = []

export function showToast(type: 'success' | 'error', title: string, body?: string) {
  const msg: ToastMessage = { id: _nextId++, type, title, body }
  listeners.forEach((fn) => fn(msg))
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  useEffect(() => {
    const handler = (msg: ToastMessage) => {
      setToasts((prev) => [...prev, msg])
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== msg.id)), 5000)
    }
    listeners.push(handler)
    return () => { const i = listeners.indexOf(handler); if (i !== -1) listeners.splice(i, 1) }
  }, [])

  if (toasts.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto flex items-start gap-3 rounded-lg shadow-lg border px-4 py-3 min-w-[280px] max-w-sm
            animate-[slideIn_0.2s_ease-out]
            ${t.type === 'success'
              ? 'bg-white border-green-300'
              : 'bg-white border-red-300'}`}
        >
          {t.type === 'success'
            ? <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5" />
            : <XCircle    size={18} className="text-red-500   shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${t.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{t.title}</p>
            {t.body && <p className="text-xs text-gray-500 mt-0.5">{t.body}</p>}
          </div>
          <button
            onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
            className="text-gray-400 hover:text-gray-600 shrink-0 mt-0.5"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  )
}
